import { z } from 'zod'

import { sameSet } from '@/lib/group-areas'
import { selectionLabels, type FirmBranchSelection } from '@/lib/firm-branch-scope'
import { messages } from '@/lib/messages'
import type { Group, ScopeDomain } from '@/types/models'

const t = messages.groups

/** Grup adı ve açıklaması (`PATCH /groups/{id}`); sınırlar backend `GroupName` / `GroupDescription` ile aynıdır. */
export const groupInfoSchema = z.object({
  name: z.string().trim().min(1, t.nameRequired).max(100, t.nameTooLong),
  description: z.string().max(500, t.descriptionTooLong),
})

export type GroupInfoValues = z.infer<typeof groupInfoSchema>

export function groupInfoValues(group: Group | null): GroupInfoValues {
  return { name: group?.name ?? '', description: group?.description ?? '' }
}

export const SCOPE_DOMAINS: readonly ScopeDomain[] = ['hr', 'finans', 'kantar']

/** İzin/kapsam ekranının form değerleri: seçili izin anahtarları + alan başına firma/şube seçimi. */
export interface GroupAccessValues {
  permissions: string[]
  scopes: Record<ScopeDomain, FirmBranchSelection>
}

/**
 * Seçimler kümedir; RHF dizileri sırayla karşılaştırdığı için hepsi sıralı tutulur (bir izni kapatıp
 * yeniden açmak kirli saymaz).
 */
export function canonicalSelection(selection: FirmBranchSelection): FirmBranchSelection {
  return {
    firms: [...selection.firms].sort(),
    branches: [...selection.branches].sort((a, b) =>
      `${a.firma}\u0000${a.sube}`.localeCompare(`${b.firma}\u0000${b.sube}`),
    ),
  }
}

export function canonicalPermissions(keys: readonly string[]): string[] {
  return [...keys].sort()
}

export function groupAccessValues(group: Group): GroupAccessValues {
  return {
    permissions: canonicalPermissions(group.permission_keys),
    scopes: {
      hr: canonicalSelection(group.scopes.hr),
      finans: canonicalSelection(group.scopes.finans),
      kantar: canonicalSelection(group.scopes.kantar),
    },
  }
}

function sameSelection(a: FirmBranchSelection, b: FirmBranchSelection): boolean {
  return sameSet(selectionLabels(a), selectionLabels(b))
}

/** Kaydedilen gruba göre değişen parçalar: bölünmüş kaydetme yalnız bunları gönderir. */
export function changedAccess(values: GroupAccessValues, base: Group) {
  return {
    permissions: !sameSet(values.permissions, base.permission_keys),
    scopes: SCOPE_DOMAINS.filter(
      (domain) => !sameSelection(values.scopes[domain], base.scopes[domain]),
    ),
  }
}
